import React from 'react'
import {
  GetReportDetailsQuery,
  GetReportFavoriteDetailsQuery,
  ReportElement as GqlReportElement,
  ReportFavoriteElement,
  useRemoveReportFavoriteMutation,
  useSetReportFavoriteMutation,
} from 'gqlTypes'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import StarIcon from '@mui/icons-material/Star'
import StarOutlineIcon from '@mui/icons-material/StarOutline'
import { getCurrentSection } from 'util/getSection'

interface Props {
  report:
    | NonNullable<GetReportDetailsQuery['report']>
    | NonNullable<GetReportFavoriteDetailsQuery['reportWithFavorites']>
  id: string
  type: ReportFavoriteElement
  sectionId: string
  element?: GqlReportElement
  favorites?: boolean
  onUpdate: () => void
}

const ReportElementFavorite: React.FC<Props> = ({
  report,
  id,
  type,
  sectionId,
  element,
  favorites = false,
  onUpdate,
}) => {
  const saveTooltip =
    type === ReportFavoriteElement.Section
      ? 'Bookmark this section'
      : 'Bookmark this element'

  const savedTooltip =
    type === ReportFavoriteElement.Section
      ? 'This section has been bookmarked'
      : 'This element has been bookmarked'

  const currentSection = getCurrentSection(report.sections, sectionId)

  let isFavorite = false
  if (currentSection) {
    isFavorite =
      type === ReportFavoriteElement.Section
        ? Boolean(currentSection.reportFavorite) ||
          currentSection.hasAllElementFavorites
        : Boolean(element?.hasSectionFavorite) ||
          Boolean(element?.reportFavorite)
  }

  const [setReportFavoriteMutation, { data, loading, error }] =
    useSetReportFavoriteMutation({
      variables: {
        id: id,
        type: type,
      },
    })

  const [
    removeReportFavoriteMutation,
    { data: removeData, loading: removeLoading, error: removeError },
  ] = useRemoveReportFavoriteMutation({
    variables: {
      id: id,
      type: type,
    },
  })

  const handleSetFavorite = () => {
    setReportFavoriteMutation().then((rslt) => {
      onUpdate()
    })
  }

  const handleRemoveFavorite = () => {
    removeReportFavoriteMutation().then((rslt) => {
      onUpdate()
    })
  }

  if (isFavorite) {
    return (
      <Tooltip title={savedTooltip}>
        <Box display="flex" alignItems="center">
          {type === ReportFavoriteElement.Section && (
            <Typography pr={0.5} component="small" sx={{ fontSize: '0.9rem' }}>
              Bookmark All
            </Typography>
          )}
          <IconButton onClick={handleRemoveFavorite}>
            <StarIcon color="primary" />
          </IconButton>
        </Box>
      </Tooltip>
    )
  }

  if (!favorites) {
    return (
      <Tooltip title={saveTooltip}>
        <Box display="flex" alignItems="center">
          {type === ReportFavoriteElement.Section && (
            <Typography pr={0.5} component="small" sx={{ fontSize: '0.9rem' }}>
              Bookmark All
            </Typography>
          )}
          <IconButton onClick={handleSetFavorite}>
            <StarOutlineIcon color="primary" />
          </IconButton>
        </Box>
      </Tooltip>
    )
  }

  return null
}

export default ReportElementFavorite
